<template>
  <div class="before-pay-info" v-if="accountInfo">
    <!-- USER HAS PAID FULL BALANCE -->
    <b-row v-if="!showOnlinePayments && accountInfo.balanceCostNoSymbol === '0.00'">
      <b-col>
        <p class="text-center p-1">
          You currently have no outstanding payments to make on this booking
        </p>
      </b-col>
    </b-row>

    <!-- USER NOT ELIGIBLE TO MAKE ONLINE PAYMENT -->

    <b-row v-else-if="!showOnlinePayments">
      <b-col>
        <p>It is not possible to confirm your booking with us online.</p>
        <p>
          Please call us or visit one of our
          <a href="/contact" target="_new">local travel centres</a>
          to pay for your booking via one of the following methods: Debit Card,
          Credit Card (Visa, MasterCard, American Express or Diners Club), Cash,
          or Cheque (Made payable to Trailfinders Ltd and subject to six working
          days clearance).
        </p>
      </b-col>
    </b-row>

    <!-- USER HAS PENDING ONLINE PAYMENT  -->

    <b-row v-else>
      <!-- MAIN BOOKINGS -->
      <div v-if="accountInfo.fil === 'M'">
        <!-- NO OUTSTANDING BALANCE -->
        <div v-if="
          accountInfo.balanceCostNoSymbol !== null &&
          accountInfo.balanceCostNoSymbol === '0.00'
        ">
          <b-col>
            <p class="text-center p-1">
              You currently have no outstanding payments to make on this booking
            </p>
          </b-col>
        </div>
        <!-- HAS OUTSTANDING BALANCE -->
        <div v-else>
          <b-col>
            <div class="before-payment-content">
              <h4 class="before-payment-text">
                <span class="before-payment-icon">
                  <i class="material-icons pr-3">report_problem</i> </span>Before making payment, please read the
                following information
              </h4>
            </div>
            <div class="mt-4 mb-3">
              <h5><strong>Essentials</strong></h5>
              <p>
                Visit your Trip Hub (ViewTrail Home > Trip Hub > Essentials) to
                ensure you are fully aware of
                <strong @click="openVisaModal" class="link-style">
                  entry requirements
                </strong>
                and
                <strong @click="openTravelHealthModal" class="link-style">
                  travel health
                </strong>
                relating to your trip.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Travel Arrangements</strong></h5>
              <p>
                Take the time to check that the details of your itinerary are
                correct - you may have made amendments or had schedule changes
                to your flights since you booked.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Insurance</strong></h5>
              <p>
                If you have not already taken out
                <a :href="`https://www.trailfinders.${bookingInfo.booking_site}/insurance#/`" target="_new">
                  <strong>insurance</strong>
                </a>
                for your trip, make this a priority to ensure that you are
                covered for both cancellation and travel.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Not Included</strong></h5>
              <p>
                Unless specified on your itinerary the following are not
                included: meals, tips, entrance fees, hotel parking charges,
                resort fees payable locally, specific airline seating, airport
                departure taxes payable locally, additional luggage fees,
                additional car hire fees (such as upgrades, excess reductions,
                one way fees, child seats, or GPS hire), and travel insurance.
                This list is a general guide and is not exhaustive.
              </p>
            </div>
            <div v-if="!hasEarlyTicketBlacklist" class="my-3">
              <h5><strong>Seating</strong></h5>
              <p>
                Please ensure you have read the 
                <a href="#/payment-hub" @click="openSeatingMessagesModal">
                  <strong>airline seating policy</strong>
                </a> 
                relating to your flights. <br/>
                Should the airline seating policy require you to have your airline 
                tickets issued, you can instruct us to issue them by clicking 
                the box below. This requires full payment of your trip.<br/>
                Once full payment is received, you will receive confirmation of 
                your airline ticket numbers via e-mail within 24 hours, allowing 
                you to pre-book seating with the airline.
              </p>
            </div>
            <!-- Checkbox if client wants to pay to issue flights early & pay for seating. This is only available for certain seating codes -->
            <!-- Hidden if at least 1 airline exists in the early ticket issue blacklist -->
            <b-form-checkbox v-if="containsSeatBookableAfterIssue && getFil === 'M' && !hasEarlyTicketBlacklist" v-model="issueFlights"
              name="issue-flights-checkbox" :value="true" :unchecked-value="false" class="mt-4"
              @change="handleIssueFlightsChecked">
              Issue my airline tickets
            </b-form-checkbox>

            <b-form-checkbox v-model="confirmationCheckBox" name="confirmation-checkbox" value="true"
              unchecked-value="false" class="mt-4" @change="checkBoxUpdate">
              * I confirm that the details of my itinerary are correct and that
              I have understood entry requirements and travel health advice for
              my trip.
            </b-form-checkbox>
            <div class="d-flex justify-content-center my-4">
              <b-button variant="primary" :disabled="confirmationCheckBox === 'false'" @click="$emit('nextModal', 2)">
                Proceed
              </b-button>
            </div>
          </b-col>
        </div>
      </div>

      <!-- PROV BOOKINGS -->
      <div v-if="accountInfo.fil === 'P'">
        <!-- FLIGHTS ONLY BOOKING -->
        <div v-if="accountInfo.flightCost !== false">
          <b-col>
            <div class="before-payment-content">
              <h4 class="before-payment-text">
                <span class="before-payment-icon">
                  <i class="material-icons pr-3">report_problem</i> </span>Before confirming your booking and making a
                payment, please
                read the following information
              </h4>
            </div>
            <div class="mt-4 mb-3">
              <h5><strong>Essentials</strong></h5>
              <p>
                Visit your Trip Hub (ViewTrail Home > Trip Hub > Essentials) to
                ensure you are fully aware of
                <strong @click="openVisaModal" class="link-style">
                  entry requirements
                </strong>
                and
                <strong @click="openTravelHealthModal" class="link-style">
                  travel health
                </strong>
                relating to your trip. We also recommend that you have adequate
                <a :href="`https://www.trailfinders.${bookingInfo.booking_site}/insurance#/`" target="_new">
                  <strong>insurance</strong>
                </a>
                cover in the event of cancellation.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Travel Arrangements</strong></h5>
              <p>
                Take the time to check that the details of your itinerary,
                including departure and arrival times and any connection
                durations. Please also note the baggage allowance associated
                with your flights.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Not Included</strong></h5>
              <p>
                Specific airline seating unless specified, airport departure
                taxes payable locally, additional luggage fees, and travel
                insurance unless specified. This list is a general guide and is
                not exhaustive.
              </p>
            </div>
            <!-- INS CHECKBOX -->
            <div v-if="accountInfo.insquoteamount && showOnlinePayments && getTerritory() === 'UK'" class="mt-2">
              <b-form-checkbox id="insurance-checkbox" v-model="insuranceCheckBox" name="insurance-checkbox"
                value="true" unchecked-value="false" size="lg" @change="openInsModal">
                <h5>
                  <strong>It is important that you have suitable travel insurance in
                    place from the moment you book your trip. Trailfinders tried
                    and tested insurance is available for
                    {{ bookingInfo.booking_currency
                    }}{{ accountInfo.insquoteamount }}
                    inclusive of insurance premium tax to cover you for this
                    trip. If you would like to include
                    <label class="paylink" style="cursor: pointer; margin-bottom: 0; color: #9cc06e"
                      @click="openInsPolicyDoc()">TF Travel Insurance</label>
                    in your trip to ensure you are covered not just for travel,
                    but also for cancellation, please tick this box.</strong>
                </h5>
              </b-form-checkbox>
            </div>

            <!-- Checkbox if client wants to pay to issue flights early & pay for seating. This is only available for certain seating codes -->

            <b-form-checkbox v-if="containsSeatBookableAfterIssue && accountInfo.fil === 'M'" v-model="issueFlights"
              name="issue-flights-checkbox" :value="true" :unchecked-value="false" class="mt-4"
              @change="handleIssueFlightsChecked">
              Issue my flights
            </b-form-checkbox>


            <b-form-checkbox v-model="confirmationCheckBox" name="confirmation-checkbox" value="true"
              unchecked-value="false" class="mt-4" @change="checkBoxUpdate">
              * I confirm that the details of my itinerary are correct and that
              I have understood entry requirements and travel health advice for
              my trip.
            </b-form-checkbox>

            <div class="d-flex justify-content-center my-4">
              <b-button variant="primary" :disabled="confirmationCheckBox === 'false'"
                @click="$emit('nextProvModal', 1)">
                Proceed
              </b-button>
            </div>
          </b-col>
        </div>

        <!-- NORMAL PROV BOOKING -->
        <div v-else>
          <b-col>
            <div class="before-payment-content">
              <h4 class="before-payment-text">
                <span class="before-payment-icon">
                  <i class="material-icons pr-3">report_problem</i> </span>Before confirming your booking and making a
                payment, please
                read the following information
              </h4>
            </div>
            <div class="mt-4 mb-3">
              <h5><strong>Essentials</strong></h5>

              <p>
                Visit your Trip Hub (ViewTrail Home > Trip Hub > Essentials) to
                ensure you are fully aware of
                <strong @click="openVisaModal" class="link-style">
                  entry requirements
                </strong>
                and
                <strong @click="openTravelHealthModal" class="link-style">
                  travel health
                </strong>
                relating to your trip. We also recommend that you have adequate
                <a :href="`https://www.trailfinders.${bookingInfo.booking_site}/insurance#/`" target="_new">
                  <strong>insurance</strong>
                </a>
                cover in the event of cancellation.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Travel Arrangements</strong></h5>
              <p>
                Take the time to check that the details of your itinerary are
                correct. This includes any gaps where you may be making your own
                arrangements, car hire rental length, flight timings, tour
                durations, and hotel stays. You should check your itinerary
                thoroughly prior to making payment.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Availability</strong></h5>
              <p>
                Your proposed itinerary is subject to confirmation at the price
                quoted. We are unable to typically hold accommodation or tours
                at the point of quoting, therefore availability of
                accommodations and services within this provisional itinerary
                will only be sought once payment has been made. In the event
                some of the arrangements are no longer available, we will
                endeavour to find a suitable alternative.
              </p>
            </div>
            <div class="my-3">
              <h5><strong>Not Included</strong></h5>
              <p>
                Unless specified on your itinerary the following are not
                included: meals, tips, entrance fees, hotel parking charges,
                resort fees payable locally, specific airline seating, airport
                departure taxes payable locally, additional luggage fees,
                additional car hire fees (such as upgrades, excess reductions,
                one way fees, child seats, or GPS hire), and travel insurance.
                This list is a general guide and is not exhaustive.
              </p>
            </div>
            <!-- INS CHECKBOX -->
            <div v-if="accountInfo.insquoteamount && showOnlinePayments && getTerritory() === 'UK'" class="mt-4">
              <b-form-checkbox id="insurance-checkbox" name="insurance-checkbox" v-model="insuranceCheckBox"
                value="true" unchecked-value="false" size="lg" @change="openInsModal">
                <h5>
                  <strong>It is important that you have suitable travel insurance in
                    place from the moment you book your trip. Trailfinders tried
                    and tested insurance is available for
                    {{ bookingInfo.booking_currency
                    }}{{ accountInfo.insquoteamount }}
                    inclusive of insurance premium tax to cover you for this
                    trip. If you would like to include
                    <label class="paylink" style="cursor: pointer; margin-bottom: 0; color: #9cc06e"
                      @click="openInsPolicyDoc()">TF Travel Insurance</label>
                    in your trip to ensure you are covered not just for travel,
                    but also for cancellation, please tick this box.</strong>
                </h5>
              </b-form-checkbox>
            </div>
            <!-- Checkbox if client wants to pay to issue flights early & pay for seating. This is only available for certain seating codes -->
            <b-form-checkbox v-if="containsSeatBookableAfterIssue && accountInfo.fil === 'M'" v-model="issueFlights"
              name="issue-flights-checkbox" :value="true" :unchecked-value="false" class="mt-4"
              @change="handleIssueFlightsChecked">
              Issue my flights
            </b-form-checkbox>

            <!-- CONFIRM TERMS CHECKBOX -->
            <b-form-checkbox v-model="confirmationCheckBox" name="confirmation-checkbox" value="true"
              unchecked-value="false" class="mt-4" @change="checkBoxUpdate">
              * I confirm that the details of my itinerary are correct and that
              I have understood entry requirements and travel health advice for
              my trip.
            </b-form-checkbox>

            <div class="d-flex justify-content-center my-4">
              <b-button variant="primary" :disabled="confirmationCheckBox === 'false'"
                @click="$emit('nextProvModal', 1)">
                Proceed
              </b-button>
            </div>
          </b-col>
        </div>
      </div>
      <InsuranceConfirmation ref="InsuranceConfirmation" @emitedInsConfirmation="setInsCheckBoxState" />
      <Visa ref="VisaRef" />
      <TravelHealth ref="TravelHealthRef" />
      <SeatingMessagesModal 
        ref="SeatingMessagesModalRef" 
        @openDetailedFlightsInfo="openDetailedFlightsInfo"
        @issue-flights-response="checkIssueFlightsAgreed" 
      />
      <DetailedFlights ref="DetailedFlightsRef" />
    </b-row>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { paymentConfirm } from '../services'
import InsuranceConfirmation from '../insurance-confirmation'
import Visa from '@/modules/menu-bar/essential-info/visa'
import TravelHealth from '@/modules/menu-bar/essential-info/travel-health'
import SeatingMessagesModal from "@/modules/navigation/modals/seating-messages"
import DetailedFlights from "@/modules/navigation/modals/detailed-flights"

export default {
  components: {
    InsuranceConfirmation,
    Visa,
    TravelHealth,
    SeatingMessagesModal,
    DetailedFlights
  },
  props: {
    precheckIssueFlights: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      insuranceCheckBox: 'false',
      insTermsCheckBox: 'false',
      confirmationCheckBox: 'false',
      paymentCost: 0,
      bookingNames: null,
      issueFlights: false,
    }
  },
  mounted() {
    if (this.precheckIssueFlights) {
      this.issueFlights = true
    }
    if (this.hasAddedInsurance) {
      this.insuranceCheckBox = 'true'
    }
  },
  computed: {
    ...mapGetters({
      accountInfo: 'booking/getAccount',
      booking: 'booking/getBooking',
      bookingNo: 'booking/getBookingNumber',
      bookingInfo: 'booking/getBookingInfo',
      essentialInfoMenu: 'essentialInfoMenu/getMenu',
      hasAddedInsurance: "navigation/getAddedInsurance",
      containsSeatBookableAfterIssue: 'flights/getDoFlightsIncludeSeatBookableAfterIssue',
      getFil: "booking/getFil",
      hasEarlyTicketBlacklist: "flights/getHasEarlyTicketIssueBlacklist",
    }),
    showOnlinePayments() {
      if (
        (this.accountInfo.paybal_wbt === 'true' &&
          this.accountInfo.paybal_menu === 'false') ||
        (this.accountInfo.paybal_wbt === 'false' &&
          this.accountInfo.paybal_menu === 'false') ||
        (this.accountInfo.paybal_wbt === 'true' &&
          this.accountInfo.paybal_menu === 'false')
      ) {
        this.$emit('emitHideStepsText', true)
        return false
      }
      this.$emit('emitHideStepsText', false)
      return true
    },
  },
  methods: {
    ...mapActions({
      login: 'auth/loginBookingNumberAction',
      addedInsurance: 'navigation/setAddedInsurance',
    }),
    getTerritory() {
      return this.bookingInfo.booking_site === "com" ? "UK" : "IE";
    },
    async showModal() {
      this.$refs['make-payment-ref'].show()
    },
    closeModal() {
      this.confirmationCheckBox = 'false'
      this.$refs['make-payment-ref'].hide()
    },
    openInsModal() {
      if (this.insuranceCheckBox === 'true') {
        this.insTermsCheckBox = 'false'
        this.$refs.InsuranceConfirmation.showModal()
      } else {
        this.setInsCheckBoxState('false')
      }
    },
    setInsCheckBoxState(val) {
      if (val === 'true') {
        this.insuranceCheckBox = 'true'
        this.addedInsurance(true)
      } else {
        this.insuranceCheckBox = 'false'
        this.addedInsurance(false)
      }
    },
    async checkBoxUpdate(confirmationCheckBox) {
      if (confirmationCheckBox === 'true') {
        await paymentConfirm(
          this.booking.booking_information.lead_surname.toLowerCase(),
          this.bookingNo
        )
      }
    },
    openInsPolicyDoc() {
      let url = ''
      if (
        this.booking.user_information.bookingNumber.charAt(0).toLowerCase() ===
        'r'
      ) {
        url =
          'https://www.trailfinders.ie/travel-insurance/iepolicy' +
          new Date().getFullYear() +
          'v3.pdf'
      } else {
        url =
          'https://www.trailfinders.com/travel-insurance/ukpolicy' +
          new Date().getFullYear() +
          'v3.pdf'
      }
      window.open(url, '_blank')
    },
    openVisaModal() {
      for (const [index, item] of this.essentialInfoMenu.entries()) {
        if (item.partial === 'VisaRef') {
          this.$refs.VisaRef.showModal(this.essentialInfoMenu[index])
          this.$root.$on('visaadvice', () => {
            this.$refs.VisaRef.showModal(this.essentialInfoMenu[index])
          })
        }
      }
    },
    openTravelHealthModal() {
      for (const [index, item] of this.essentialInfoMenu.entries()) {
        if (item.partial === 'TravelHealthRef') {
          this.$refs.TravelHealthRef.showModal(this.essentialInfoMenu[index])
          this.$root.$on('travelhealth', () => {
            if (
              this.$refs &&
              this.$refs.TravelHealthRef &&
              this.$refs.TravelHealthRef.showModal
            ) {
              this.$refs.TravelHealthRef.showModal(
                this.essentialInfoMenu[index]
              )
            }
          })
        }
      }
    },
    handleIssueFlightsChecked(isChecked) {
      if (isChecked) {
        this.$refs.SeatingMessagesModalRef.showModal({ from: 'paymentModal', issueTickets: true })
      } else {
        this.issueFlights = false
        this.$emit('issueFlightsChange', this.issueFlights)
      }
    },
    checkIssueFlightsAgreed(hasAgreed) {
      this.issueFlights = hasAgreed // will uncheck the 'issue my flights checkbox' unless agreed in the seating messages modal
      this.$emit('issueFlightsChange', this.issueFlights)
    },
    openDetailedFlightsInfo() {
      this.$refs.DetailedFlightsRef.showModal()
    },
    openSeatingMessagesModal() {
      this.$refs.SeatingMessagesModalRef.showModal()
    }
  },
}
</script>
<style lang="scss">
@import './styles.scss';
</style>
